import { useEffect, useRef } from 'react';
import type { IndustryType } from '@/types';
import { MODEL_LABELS, INDUSTRY_TYPE_ORDER } from '@/lib/constants';
import { trackFilterChanged } from '@/lib/analytics';

interface FilterAnalyticsArgs {
  isLoading: boolean;
  selectedCompanyIds: string[];
  selectedGroupIds: string[];
  selectedModels: string[];
  selectedIndustryTypes: IndustryType[];
  companiesCount: number;
  groupsCount: number;
}

/**
 * Fires trackFilterChanged when a selection changes, skipping the initial load.
 * Extracted verbatim from FilterProvider (ISS-030). Each effect must fire only
 * on ITS selection array — matching the originals exactly — so isLoading and the
 * counts are read through latest-value refs rather than listed as dependencies
 * (listing them would fire the effect on unrelated changes).
 */
export function useFilterAnalytics({
  isLoading,
  selectedCompanyIds,
  selectedGroupIds,
  selectedModels,
  selectedIndustryTypes,
  companiesCount,
  groupsCount,
}: FilterAnalyticsArgs) {
  const filterInitializedRef = useRef(false);

  // Latest values read by the tracking effects without triggering them.
  const isLoadingRef = useRef(isLoading);
  const companiesCountRef = useRef(companiesCount);
  const groupsCountRef = useRef(groupsCount);
  useEffect(() => {
    isLoadingRef.current = isLoading;
    companiesCountRef.current = companiesCount;
    groupsCountRef.current = groupsCount;
  }, [isLoading, companiesCount, groupsCount]);

  useEffect(() => {
    if (isLoadingRef.current) return;
    if (!filterInitializedRef.current) {
      filterInitializedRef.current = true;
      return; // Skip tracking the initial load
    }
    trackFilterChanged('company', selectedCompanyIds.length, companiesCountRef.current);
  }, [selectedCompanyIds]);

  useEffect(() => {
    if (!filterInitializedRef.current) return;
    trackFilterChanged('group', selectedGroupIds.length, groupsCountRef.current);
  }, [selectedGroupIds]);

  useEffect(() => {
    if (!filterInitializedRef.current) return;
    trackFilterChanged('model', selectedModels.length, Object.keys(MODEL_LABELS).length);
  }, [selectedModels]);

  useEffect(() => {
    if (!filterInitializedRef.current) return;
    trackFilterChanged('industry', selectedIndustryTypes.length, INDUSTRY_TYPE_ORDER.length);
  }, [selectedIndustryTypes]);
}
